import React, { useState, useEffect } from 'react';
import classes from './User.module.css';
import axiosInstance from '../../../Configuration/axiosInstance';
import LoadingSpinner from '../../../Components/Shared/Loader/Loader';

const User = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [msg, setMsg] = useState('');
  const [dialogBox, setDialogBox] = useState(false);
  const [isError, setIsError] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'user'
  });

  // Fetch all users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/user');
      setUsers(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      showMessage('Failed to fetch users', true);
      setLoading(false);
    }
  };

  const showMessage = (message, isError = false) => {
    setMsg(message);
    setIsError(isError);
    setDialogBox(true);
    setTimeout(() => {
      setDialogBox(false);
      setMsg('');
      setIsError(false);
    }, 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // ✅ FIXED: Properly handle add user button click
  const handleAddUserClick = () => {
    setEditingUser(null); // ✅ Clear editing user first
    setFormData({
      email: '',
      password: '',
      role: 'user'
    });
    setShowAddForm(true); // ✅ Always show form in add mode
  };

  // ✅ ENHANCED: Reset form function with better state management
  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      role: 'user'
    });
    setShowAddForm(false);
    setEditingUser(null); // ✅ Always clear editing user
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      showMessage('Email and password are required', true);
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post('/user', formData);
      showMessage(response.data.message);
      resetForm();
      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMsg = error.response?.data?.message || 'Failed to create user';
      showMessage(errorMsg, true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    if (!editingUser) return;

    try {
      setLoading(true);
      const updateData = {};
      
      // Only include fields that have values or have changed
      if (formData.role !== editingUser.role) {
        updateData.role = formData.role;
      }
      
      if (formData.email && formData.email !== editingUser.email) {
        updateData.email = formData.email;
      }

      // ✅ NEW: Include password if provided
      if (formData.password && formData.password.trim() !== '') {
        updateData.password = formData.password;
      }

      const response = await axiosInstance.patch(`/user/${editingUser._id}`, updateData);
      showMessage(response.data.message);
      resetForm();
      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Error updating user:', error);
      const errorMsg = error.response?.data?.message || 'Failed to update user';
      showMessage(errorMsg, true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user: ${userEmail}?`)) {
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.delete(`/user/${userId}`);
      showMessage('User deleted successfully');
      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMsg = error.response?.data?.message || 'Failed to delete user';
      showMessage(errorMsg, true);
    } finally {
      setLoading(false);
    }
  };

  // ✅ ENHANCED: Start edit function with proper state management
  const startEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '', // Don't populate password for security
      role: user.role
    });
    setShowAddForm(true); // ✅ Show form in edit mode
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return classes.adminBadge;
      case 'delivery': return classes.deliveryBadge;
      default: return classes.userBadge;
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h2>User Management</h2>
        {/* ✅ FIXED: Use proper handler for add user button */}
        <button   
          className={classes.addButton}
          onClick={handleAddUserClick}
        >
          ➕ Add New User
        </button>
      </div>

      {/* Message Dialog */}
      {dialogBox && (
        <div className={classes.dialogOverlay}>
          <div className={`${classes.dialog} ${isError ? classes.errorDialog : classes.successDialog}`}>
            <div className={classes.dialogContent}>
              <span className={classes.dialogIcon}>
                {isError ? '❌' : '✅'}
              </span>
              <p>{msg}</p>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit User Form */}
      {showAddForm && (
        <div 
          className={classes.formContainer}
          data-mode={editingUser ? "edit" : "add"}
        >
          {/* ✅ IMPROVED: More explicit title based on mode */}
          <div className={classes.formHeader}>
            <h3>
              {editingUser ? (
                <>✏️ Edit User: {editingUser.email}</>
              ) : (
                <>➕ Add New User</>
              )}
            </h3>
            {/* ✅ NEW: Add a close button for better UX */}
            <button 
              type="button" 
              onClick={resetForm} 
              className={classes.closeFormButton}
              title="Close form"
            >
              ❌
            </button>
          </div>

          <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
            <div className={classes.formGroup}>
              <label>Email:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required={!editingUser}
                placeholder="user@company.com"
              />
            </div>

            <div className={classes.formGroup}>
              <label>
                Password:
                {editingUser && (
                  <small style={{ color: '#6c757d', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                    (Leave blank to keep current password)
                  </small>
                )}
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required={!editingUser}
                placeholder={editingUser ? "Enter new password (optional)" : "Enter password"}
              />
            </div>

            <div className={classes.formGroup}>
              <label>Role:</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
              >
                <option value="user">👤 User</option>
                <option value="admin">👑 Admin</option>
                <option value="delivery">🚚 Delivery</option>
              </select>
            </div>

            <div className={classes.formActions}>
              <button type="submit" className={classes.submitButton} disabled={loading}>
                {loading ? '⏳ Processing...' : (editingUser ? '💾 Update User' : '✅ Create User')}
              </button>
              <button type="button" onClick={resetForm} className={classes.cancelButton}>
                ❌ Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      {loading ? (
        <div className={classes.loaderContainer}>
          <LoadingSpinner />
        </div>
      ) : (
        <div className={classes.usersContainer}>
          <div className={classes.usersHeader}>
            <h3>📋 All Users ({users.length})</h3>
          </div>

          {users.length === 0 ? (
            <div className={classes.noUsers}>
              <p>No users found. Add your first user!</p>
            </div>
          ) : (
            <div className={classes.usersGrid}>
              {users.map((user) => (
                <div key={user._id} className={classes.userCard}>
                  <div className={classes.userInfo}>
                    <div className={classes.userEmail}>
                      📧 {user.email}
                    </div>
                    <div className={classes.userDetails}>
                      <span className={`${classes.roleBadge} ${getRoleBadgeClass(user.role)}`}>
                        {user.role.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  
                  <div className={classes.userActions}>
                    <button 
                      className={classes.editButton}
                      onClick={() => startEdit(user)}
                      title="Edit user"
                    >
                      ✏️
                    </button>
                    <button 
                      className={classes.deleteButton}
                      onClick={() => handleDeleteUser(user._id, user.email)}
                      title="Delete user"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default User;